import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Feather, Loader2, Languages } from "lucide-react";
import SocialAuthButtons from "@/components/SocialAuthButtons";
import PasswordInput from "@/components/PasswordInput";

export default function LoginPage() {
  const { login, error } = useAuth();
  const { t, lang, changeLanguage } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/couples", { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-cream">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1768662473131-b80660c4e03b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxleG90aWMlMjBiaXJkcyUyMHBhcnJvdCUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzc4Njk1NjU0fDA&ixlib=rb-4.1.0&q=85"
          alt="Macaw"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-espresso/30" />
        <div className="absolute bottom-10 left-10 text-white max-w-md">
          <div className="flex items-center gap-2 mb-3 opacity-90">
            <Feather className="w-5 h-5" />
            <span className="text-xs uppercase tracking-[0.3em]">Aviary Studio</span>
          </div>
          <h2 className="font-heading text-4xl font-semibold leading-tight">
            Track your breeding pairs, eggs and chicks in one calm place.
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 relative">
        <div className="absolute top-4 right-4 rtl:right-auto rtl:left-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => changeLanguage(lang === "ar" ? "en" : "ar")}
            className="border-olive/40 text-olive hover:bg-olive/10 text-xs font-semibold"
            data-testid="login-language-toggle"
          >
            <Languages className="w-3.5 h-3.5 mr-1" />
            {lang === "ar" ? "English" : "العربية"}
          </Button>
        </div>
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md space-y-6"
          data-testid="login-form"
        >
          <div>
            <div className="flex items-center gap-2 text-olive mb-2">
              <Feather className="w-5 h-5" />
              <span className="text-xs font-semibold uppercase tracking-[0.3em]">{t("app.brand")}</span>
            </div>
            <h1 className="font-heading text-4xl font-semibold text-espresso">{t("auth.welcome")}</h1>
            <p className="text-sm text-espresso/70 mt-2">{t("auth.signin_sub")}</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">{t("common.email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@aviary.com"
                data-testid="login-email-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">{t("common.password")}</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                testId="login-password-input"
                testIdSuffix="login-password"
                autoComplete="current-password"
                className="mt-1.5"
              />
            </div>
          </div>

          {error && (
            <div
              className="text-sm text-danger bg-danger/10 border border-danger/20 rounded-lg px-3 py-2"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-olive hover:bg-olive-hover text-white h-11 rounded-lg"
            data-testid="login-submit-button"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("common.signin")}
          </Button>

          <SocialAuthButtons />

          <p className="text-sm text-center">
            <Link to="/forgot-password" className="text-olive font-semibold hover:underline" data-testid="forgot-password-link">
              {t("auth.forgot_password")}
            </Link>
          </p>

          <p className="text-sm text-center text-espresso/70">
            {t("auth.new_to")}{" "}
            <Link to="/register" className="text-olive font-semibold hover:underline" data-testid="register-link">
              {t("common.signup")}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
